import { getAuthApiErrorText, parseAuthApiError } from "@/utils/authApiError";

/** i18n keys for magic-link toasts (default namespace + `translation:` prefix). */
export const magicLinkErrorKeys = {
  requestFailed: "translation:newWelcome.magicLink.errors.requestFailed",
  rateLimited: "translation:newWelcome.magicLink.errors.rateLimited",
  notAllowed: "translation:newWelcome.magicLink.errors.notAllowed",
  invalidLink: "translation:newWelcome.magicLink.errors.invalidLink",
  expiredLink: "translation:newWelcome.magicLink.errors.expiredLink",
  usedLink: "translation:newWelcome.magicLink.errors.usedLink",
  consumeFailed: "translation:newWelcome.magicLink.errors.consumeFailed",
} as const;

export type MagicLinkRequestError = "rate_limited" | "not_allowed" | "generic";

export type MagicLinkConsumeError =
  | "used_link"
  | "expired_link"
  | "invalid_link"
  | "generic";

export const getMagicLinkRequestErrorKey = (
  reason: MagicLinkRequestError,
): (typeof magicLinkErrorKeys)[keyof typeof magicLinkErrorKeys] => {
  switch (reason) {
    case "rate_limited":
      return magicLinkErrorKeys.rateLimited;
    case "not_allowed":
      return magicLinkErrorKeys.notAllowed;
    default:
      return magicLinkErrorKeys.requestFailed;
  }
};

export const getMagicLinkConsumeErrorKey = (
  reason: MagicLinkConsumeError,
): (typeof magicLinkErrorKeys)[keyof typeof magicLinkErrorKeys] => {
  switch (reason) {
    case "used_link":
      return magicLinkErrorKeys.usedLink;
    case "expired_link":
      return magicLinkErrorKeys.expiredLink;
    case "invalid_link":
      return magicLinkErrorKeys.invalidLink;
    default:
      return magicLinkErrorKeys.consumeFailed;
  }
};

export const classifyMagicLinkRequestError = (
  error: unknown,
): MagicLinkRequestError => {
  const apiError = parseAuthApiError(error);
  const message = getAuthApiErrorText(error);
  const category = apiError?.category?.toUpperCase().replace(/\s/g, "_") ?? "";

  if (
    category === "RATE_LIMIT" ||
    message.includes("maximum number of login attempts")
  ) {
    return "rate_limited";
  }

  if (message.includes("magic login not allowed")) {
    return "not_allowed";
  }

  return "generic";
};

export const isMagicLoginNotAllowedError = (error: unknown): boolean =>
  classifyMagicLinkRequestError(error) === "not_allowed";
